const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

import { enqueue, shouldQueue } from './offlineQueue';
import { isNetworkLikeError } from '../utils/safeFetch';

const getAuthHeaders = () => {
  const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {})
  };
};

// XHR fallback for GETs — Citrix proxies sometimes drop the very first
// `fetch` from a session (proxy handshake, idle warm-up). XHR doesn't go
// through the same code path, so retrying over XHR rescues these flows
// transparently.
const _xhrGetJson = (fullUrl, headers) =>
  new Promise((resolve, reject) => {
    const x = new XMLHttpRequest();
    x.open('GET', fullUrl, true);
    Object.entries(headers || {}).forEach(([k, v]) => x.setRequestHeader(k, v));
    x.responseType = 'text';
    x.onload = () => {
      if (x.status >= 200 && x.status < 300) {
        try { resolve(x.responseText ? JSON.parse(x.responseText) : null); }
        catch { reject(new Error('Bad JSON response')); }
      } else {
        let detail;
        try { detail = JSON.parse(x.responseText).detail; } catch { /* ignore */ }
        reject(new Error(detail || `HTTP ${x.status}`));
      }
    };
    x.onerror = () => reject(new Error('Network error'));
    x.send();
  });

const _sleep = (ms) => new Promise((r) => setTimeout(r, ms));

const fetchWithAuth = async (endpoint, options = {}) => {
  const fullUrl = `${API}${endpoint}`;
  const method = (options.method || 'GET').toUpperCase();
  const buildHeaders = () => ({ ...getAuthHeaders(), ...options.headers });

  // GET path: fetch → XHR fallback → one retry. This makes every read-only
  // call across the app (planning, picking, expedition, kpis…) survive
  // intermittent corporate-proxy drops without a user-visible toast.
  if (method === 'GET') {
    const tryFetchOnce = async () => {
      const r = await fetch(fullUrl, { ...options, headers: buildHeaders() });
      if (!r.ok) {
        let errorMessage = `HTTP ${r.status}`;
        try { const error = await r.json(); errorMessage = error.detail || errorMessage; }
        catch { /* ignore */ }
        throw new Error(errorMessage);
      }
      return r.json();
    };
    const tryOnce = async () => {
      try { return await tryFetchOnce(); }
      catch (e) {
        if (isNetworkLikeError(e)) return _xhrGetJson(fullUrl, buildHeaders());
        throw e;
      }
    };
    let result;
    try { result = await tryOnce(); }
    catch (e) {
      if (!isNetworkLikeError(e)) throw e;
      await _sleep(800);
      result = await tryOnce();
    }
    try { localStorage.setItem('kk-last-sync', String(Date.now())); } catch { /* quota */ }
    return result;
  }

  // Non-GET path (mutations) — preserves the offline-queue behaviour.
  let response;
  try {
    response = await fetch(fullUrl, { ...options, headers: buildHeaders() });
  } catch {
    // Network is down (or backend unreachable). For mutations that we are
    // allowed to queue, persist the request and tell the caller it was
    // accepted-for-later. The OfflineBanner / queue badge will surface it.
    if (shouldQueue(fullUrl, options)) {
      enqueue({
        url: fullUrl,
        method,
        body: options.body,
        label: `${method} ${endpoint}`,
      });
      return { __queued: true, message: 'Saved offline — will sync when back online' };
    }
    throw new Error('Network error — could not reach server');
  }

  if (!response.ok) {
    let errorMessage = `HTTP ${response.status}`;
    let errorDetail = null;
    try {
      const error = await response.json();
      errorDetail = error.detail;
      errorMessage = (typeof errorDetail === 'string' ? errorDetail : errorDetail?.reason) || errorMessage;
    } catch {
      // Ignore JSON parse errors
    }
    // 402 = paywall hit. Broadcast to the global modal listener.
    if (response.status === 402 && errorDetail && typeof errorDetail === 'object') {
      try {
        window.dispatchEvent(new CustomEvent('paywall:hit', { detail: errorDetail }));
      } catch { /* old browser — no-op */ }
    }
    throw new Error(errorMessage);
  }

  // Track most-recent successful sync — used by the OfflineBanner so users
  // know how fresh the cached data they're seeing is.
  try { localStorage.setItem('kk-last-sync', String(Date.now())); } catch { /* quota */ }

  return response.json();
};

const fetchWithAuthBlob = async (endpoint, options = {}) => {
  const fullUrl = `${API}${endpoint}`;
  const buildHeaders = () => ({ ...getAuthHeaders(), ...options.headers });

  // Mirror of fetchWithAuth's GET path — fetch → XHR fallback (on
  // network-class errors) → one 800ms retry. Same Citrix-resilience
  // story: the first request after an idle proxy session sometimes
  // drops, and Excel/PDF exports were the next surface affected.
  const _xhrBlob = () =>
    new Promise((resolve, reject) => {
      const x = new XMLHttpRequest();
      x.open('GET', fullUrl, true);
      const all = buildHeaders();
      Object.entries(all).forEach(([k, v]) => x.setRequestHeader(k, v));
      x.responseType = 'blob';
      x.onload = () => {
        if (x.status >= 200 && x.status < 300) {
          resolve(x.response);
        } else {
          reject(new Error(`HTTP ${x.status}`));
        }
      };
      x.onerror = () => reject(new Error('Network error'));
      x.send();
    });

  const tryOnce = async () => {
    try {
      const r = await fetch(fullUrl, { ...options, headers: buildHeaders() });
      if (!r.ok) {
        let errorMessage = `HTTP ${r.status}`;
        try { const error = await r.json(); errorMessage = error.detail || errorMessage; }
        catch { /* ignore */ }
        throw new Error(errorMessage);
      }
      const contentType = r.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        return r.json();
      }
      return r.blob();
    } catch (e) {
      if (isNetworkLikeError(e)) return _xhrBlob();
      throw e;
    }
  };

  try { return await tryOnce(); }
  catch (e) {
    if (!isNetworkLikeError(e)) throw e;
    await _sleep(800);
    return tryOnce();
  }
};

// Dashboard
export const getDashboardStats = () => fetchWithAuth('/dashboard/stats');
export const getLssDashboard = (params = {}) => {
  const qs = new URLSearchParams();
  if (params.picking_from) qs.append('picking_from', params.picking_from);
  if (params.picking_to) qs.append('picking_to', params.picking_to);
  const qStr = qs.toString();
  return fetchWithAuth(`/dashboard/lss-stats${qStr ? `?${qStr}` : ''}`);
};

// Projects (DMAIC)
export const getProjects = (industry) => 
  fetchWithAuth(`/projects${industry ? `?industry=${industry}` : ''}`);
export const getProject = (id) => fetchWithAuth(`/projects/${id}`);
export const createProject = (data) => 
  fetchWithAuth('/projects', { method: 'POST', body: JSON.stringify(data) });
export const updateProject = (id, data) => 
  fetchWithAuth(`/projects/${id}`, { method: 'PUT', body: JSON.stringify(data) });
export const deleteProject = (id) => 
  fetchWithAuth(`/projects/${id}`, { method: 'DELETE' });

// Audits (6S)
export const getAudits = (industry) => 
  fetchWithAuth(`/audits${industry ? `?industry=${industry}` : ''}`);
export const getAudit = (id) => fetchWithAuth(`/audits/${id}`);
export const createAudit = (data) => 
  fetchWithAuth('/audits', { method: 'POST', body: JSON.stringify(data) });
export const updateAudit = (id, data) => 
  fetchWithAuth(`/audits/${id}`, { method: 'PUT', body: JSON.stringify(data) });
export const deleteAudit = (id) => 
  fetchWithAuth(`/audits/${id}`, { method: 'DELETE' });

// Kaizen Events
export const getKaizenEvents = (industry) => 
  fetchWithAuth(`/kaizen-events${industry ? `?industry=${industry}` : ''}`);
export const getKaizenEvent = (id) => fetchWithAuth(`/kaizen-events/${id}`);
export const createKaizenEvent = (data) => 
  fetchWithAuth('/kaizen-events', { method: 'POST', body: JSON.stringify(data) });
export const updateKaizenEvent = (id, data) => 
  fetchWithAuth(`/kaizen-events/${id}`, { method: 'PUT', body: JSON.stringify(data) });
export const deleteKaizenEvent = (id) => 
  fetchWithAuth(`/kaizen-events/${id}`, { method: 'DELETE' });

// Value Stream Maps
export const getVSMs = (industry) => 
  fetchWithAuth(`/vsm${industry ? `?industry=${industry}` : ''}`);
export const getVSM = (id) => fetchWithAuth(`/vsm/${id}`);
export const createVSM = (data) => 
  fetchWithAuth('/vsm', { method: 'POST', body: JSON.stringify(data) });
export const updateVSM = (id, data) => 
  fetchWithAuth(`/vsm/${id}`, { method: 'PUT', body: JSON.stringify(data) });
export const deleteVSM = (id) => 
  fetchWithAuth(`/vsm/${id}`, { method: 'DELETE' });

// KPIs
export const getKPIs = (industry, category) => {
  const params = new URLSearchParams();
  if (industry) params.append('industry', industry);
  if (category) params.append('category', category);
  const query = params.toString();
  return fetchWithAuth(`/kpis${query ? `?${query}` : ''}`);
};
export const createKPI = (data) => 
  fetchWithAuth('/kpis', { method: 'POST', body: JSON.stringify(data) });
export const updateKPI = (id, data) => 
  fetchWithAuth(`/kpis/${id}`, { method: 'PUT', body: JSON.stringify(data) });
export const deleteKPI = (id) => 
  fetchWithAuth(`/kpis/${id}`, { method: 'DELETE' });
export const getKPIHistory = (id, days = 30) => 
  fetchWithAuth(`/kpis/${id}/history?days=${days}`);
export const getKPIAnalytics = (industry) => 
  fetchWithAuth(`/kpis/analytics/summary${industry ? `?industry=${industry}` : ''}`);
export const getKPITrends = (days = 30, industry, fromDate, toDate) => {
  const params = new URLSearchParams();
  params.append('days', days);
  if (industry) params.append('industry', industry);
  if (fromDate) params.append('from_date', fromDate);
  if (toDate) params.append('to_date', toDate);
  return fetchWithAuth(`/kpis/analytics/trends?${params.toString()}`);
};
export const getAutomatedKPIs = () => fetchWithAuth('/kpis/automated');

// Social - Posts
export const getPosts = (skip = 0, limit = 20) => 
  fetchWithAuth(`/posts?skip=${skip}&limit=${limit}`);
export const createPost = (data) => 
  fetchWithAuth('/posts', { method: 'POST', body: JSON.stringify(data) });
export const likePost = (id) => 
  fetchWithAuth(`/posts/${id}/like`, { method: 'POST' });
export const dislikePost = (id) => 
  fetchWithAuth(`/posts/${id}/dislike`, { method: 'POST' });
export const deletePost = (id) => 
  fetchWithAuth(`/posts/${id}`, { method: 'DELETE' });

// Image Upload
export const uploadImage = async (file) => {
  const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
  const formData = new FormData();
  formData.append('file', file);
  
  const response = await fetch(`${API}/upload/image`, {
    method: 'POST',
    headers: {
      ...(token ? { 'Authorization': `Bearer ${token}` } : {})
    },
    body: formData
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Upload failed');
  }
  
  return response.json();
};

// Social - Comments
export const getComments = (postId) => 
  fetchWithAuth(`/posts/${postId}/comments`);
export const createComment = (postId, data) => 
  fetchWithAuth(`/posts/${postId}/comments`, { method: 'POST', body: JSON.stringify(data) });

// Messages
export const getConversations = () => fetchWithAuth('/messages/conversations');
export const getMessages = (partnerId) => fetchWithAuth(`/messages/${partnerId}`);
export const sendMessage = (data) => 
  fetchWithAuth('/messages', { method: 'POST', body: JSON.stringify(data) });

// Templates
export const getTemplates = (type, industry) => {
  const params = new URLSearchParams();
  if (type) params.append('type', type);
  if (industry) params.append('industry', industry);
  const query = params.toString();
  return fetchWithAuth(`/templates${query ? `?${query}` : ''}`);
};
export const getTemplate = (id) => fetchWithAuth(`/templates/${id}`);
export const seedTemplates = () => 
  fetchWithAuth('/seed/templates', { method: 'POST' });

// Checklist Templates
export const getChecklistTemplates = (type, industry) => {
  const params = new URLSearchParams();
  if (type) params.append('type', type);
  if (industry) params.append('industry', industry);
  const query = params.toString();
  return fetchWithAuth(`/checklist-templates${query ? `?${query}` : ''}`);
};
export const getChecklistTemplate = (id) => fetchWithAuth(`/checklist-templates/${id}`);
export const createChecklistTemplate = (data) => 
  fetchWithAuth('/checklist-templates', { method: 'POST', body: JSON.stringify(data) });
export const updateChecklistTemplate = (id, data) => 
  fetchWithAuth(`/checklist-templates/${id}`, { method: 'PUT', body: JSON.stringify(data) });
export const deleteChecklistTemplate = (id) => 
  fetchWithAuth(`/checklist-templates/${id}`, { method: 'DELETE' });
export const seedChecklistTemplates = () => 
  fetchWithAuth('/seed/checklist-templates', { method: 'POST' });

// Checklists
export const getChecklists = (type, status, industry) => {
  const params = new URLSearchParams();
  if (type) params.append('type', type);
  if (status) params.append('status', status);
  if (industry) params.append('industry', industry);
  const query = params.toString();
  return fetchWithAuth(`/checklists${query ? `?${query}` : ''}`);
};
export const getChecklist = (id) => fetchWithAuth(`/checklists/${id}`);
export const createChecklist = (data) => 
  fetchWithAuth('/checklists', { method: 'POST', body: JSON.stringify(data) });
export const updateChecklist = (id, data) => 
  fetchWithAuth(`/checklists/${id}`, { method: 'PUT', body: JSON.stringify(data) });
export const renameChecklist = (id, name) => 
  fetchWithAuth(`/checklists/${id}/rename`, { method: 'PUT', body: JSON.stringify({ name }) });
export const completeChecklist = (id) => 
  fetchWithAuth(`/checklists/${id}/complete`, { method: 'POST' });
export const deleteChecklist = (id) => 
  fetchWithAuth(`/checklists/${id}`, { method: 'DELETE' });
export const getChecklistAnalytics = (type, industry) => {
  const params = new URLSearchParams();
  if (type) params.append('type', type);
  if (industry) params.append('industry', industry);
  const query = params.toString();
  return fetchWithAuth(`/checklists/analytics/summary${query ? `?${query}` : ''}`);
};

// Checklist Assignments (Admin)
export const createChecklistAssignment = (data) => 
  fetchWithAuth('/checklist-assignments', { method: 'POST', body: JSON.stringify(data) });

// Admin - Users
export const getAllUsers = () => fetchWithAuth('/admin/users');
export const createUser = (data) => 
  fetchWithAuth('/admin/users', { method: 'POST', body: JSON.stringify(data) });
export const updateUser = (id, data) => 
  fetchWithAuth(`/admin/users/${id}`, { method: 'PUT', body: JSON.stringify(data) });
export const blockUser = (id) => 
  fetchWithAuth(`/admin/users/${id}/block`, { method: 'PUT' });
export const unblockUser = (id) => 
  fetchWithAuth(`/admin/users/${id}/unblock`, { method: 'PUT' });
export const deleteUser = (id) => 
  fetchWithAuth(`/admin/users/${id}`, { method: 'DELETE' });
export const updateUserRole = (id, role) => 
  fetchWithAuth(`/admin/users/${id}/role`, { method: 'PUT', body: JSON.stringify({ role }) });

// Assignments
export const getAssignments = (status, assignedTo, teamId) => {
  const params = new URLSearchParams();
  if (status) params.append('status', status);
  if (assignedTo) params.append('assigned_to', assignedTo);
  if (teamId) params.append('team_id', teamId);
  const query = params.toString();
  return fetchWithAuth(`/assignments${query ? `?${query}` : ''}`);
};
export const getAssignment = (id) => fetchWithAuth(`/assignments/${id}`);
export const createAssignment = (data) => 
  fetchWithAuth('/assignments', { method: 'POST', body: JSON.stringify(data) });
export const updateAssignment = (id, data) => 
  fetchWithAuth(`/assignments/${id}`, { method: 'PUT', body: JSON.stringify(data) });
export const deleteAssignment = (id) => 
  fetchWithAuth(`/assignments/${id}`, { method: 'DELETE' });
export const getAssignmentComments = (assignmentId) => 
  fetchWithAuth(`/assignments/${assignmentId}/comments`);
export const addAssignmentComment = (assignmentId, content) => 
  fetchWithAuth(`/assignments/${assignmentId}/comments`, { method: 'POST', body: JSON.stringify({ content }) });

// Notifications
export const getNotifications = (unreadOnly = false) => 
  fetchWithAuth(`/notifications${unreadOnly ? '?unread_only=true' : ''}`);
export const getNotificationCount = () => fetchWithAuth('/notifications/count');
export const markNotificationRead = (id) => 
  fetchWithAuth(`/notifications/${id}/read`, { method: 'PUT' });
export const markAllNotificationsRead = () => 
  fetchWithAuth('/notifications/read-all', { method: 'PUT' });
export const deleteNotification = (id) => 
  fetchWithAuth(`/notifications/${id}`, { method: 'DELETE' });

// Seed
export const seedUsers = () => fetchWithAuth('/seed/users', { method: 'POST' });

// Invitations
export const createInvitation = (data) => 
  fetchWithAuth('/admin/invitations', { method: 'POST', body: JSON.stringify(data) });
export const getInvitations = (includeUsed = false) => 
  fetchWithAuth(`/admin/invitations${includeUsed ? '?include_used=true' : ''}`);
export const deleteInvitation = (id) => 
  fetchWithAuth(`/admin/invitations/${id}`, { method: 'DELETE' });
export const resendInvitation = (id) => 
  fetchWithAuth(`/admin/invitations/${id}/resend`, { method: 'POST' });

// Verify invitation code (public)
export const verifyInvitationCode = async (code) => {
  const response = await fetch(`${API}/auth/verify-code/${code}`);
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Invalid code' }));
    throw new Error(error.detail || 'Invalid code');
  }
  return response.json();
};

// Register with code (public)
export const registerWithCode = async (data) => {
  const response = await fetch(`${API}/auth/register-with-code`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Registration failed' }));
    throw new Error(error.detail || 'Registration failed');
  }
  return response.json();
};

// Pending Changes (Master Admin)
export const getPendingChanges = (status = 'pending') => 
  fetchWithAuth(`/admin/pending-changes?status=${status}`);
export const getPendingChangesCount = () => 
  fetchWithAuth('/admin/pending-changes/count');
export const approvePendingChange = (changeId, notes = '') => 
  fetchWithAuth(`/admin/pending-changes/${changeId}/approve`, { 
    method: 'POST', 
    body: JSON.stringify({ notes }) 
  });
export const rejectPendingChange = (changeId, notes = '') => 
  fetchWithAuth(`/admin/pending-changes/${changeId}/reject`, { 
    method: 'POST', 
    body: JSON.stringify({ notes }) 
  });

// Activity Log (Master Admin)
export const getActivityLog = (userId = null, action = null, resourceType = null, limit = 100) => {
  const params = new URLSearchParams();
  if (userId) params.append('user_id', userId);
  if (action) params.append('action', action);
  if (resourceType) params.append('resource_type', resourceType);
  params.append('limit', limit);
  return fetchWithAuth(`/admin/activity-log?${params.toString()}`);
};

export const deleteActivityLogs = (beforeDate = null) => {
  const params = beforeDate ? `?before_date=${beforeDate}` : '';
  return fetchWithAuth(`/admin/activity-log${params}`, { method: 'DELETE' });
};

// Delete a single activity-log row (Master Admin only). Server logs
// the deletion itself as a new event so the audit trail still
// documents what was scrubbed (without retaining the scrubbed details).
export const deleteActivityLogEntry = (logId) =>
  fetchWithAuth(`/admin/activity-log/${logId}`, { method: 'DELETE' });

// Archive
export const getArchivedResources = () => 
  fetchWithAuth('/archived');
export const archiveResource = (type, id) => 
  fetchWithAuth(`/archive/${type}/${id}`, { method: 'POST' });
export const unarchiveResource = (type, id) => 
  fetchWithAuth(`/unarchive/${type}/${id}`, { method: 'POST' });
export const archivePlanningWeek = (weekId) =>
  fetchWithAuth(`/planning/archive-week/${weekId}`, { method: 'POST' });
export const unarchivePlanningWeek = (weekId) =>
  fetchWithAuth(`/planning/archive-week/${weekId}`, { method: 'DELETE' });
export const getArchivedPlanningWeeks = () =>
  fetchWithAuth('/planning/archived-weeks');
export const getPlanningArchiveStatus = (weekId) =>
  fetchWithAuth(`/planning/archive-status/${weekId}`);
export const getWeekBriefing = (weekId) =>
  fetchWithAuth(`/planning/briefing/${weekId}`);
export const sendWeekBriefing = (weekId) =>
  fetchWithAuth(`/planning/briefing/${weekId}/send`, { method: 'POST' });

// Export Individual Items
export const exportProject = (projectId, format = 'json') => 
  fetchWithAuthBlob(`/export/project/${projectId}?format=${format}`);
export const exportProjectPDF = (projectId) => 
  fetchWithAuthBlob(`/export/project/${projectId}/pdf`);
export const exportAudit = (auditId, format = 'json') => 
  fetchWithAuthBlob(`/export/audit/${auditId}?format=${format}`);
export const exportAuditPDF = (auditId) =>
  fetchWithAuthBlob(`/export/audit/${auditId}/pdf`);
export const exportChecklist = (checklistId, format = 'json') => 
  fetchWithAuthBlob(`/export/checklist/${checklistId}?format=${format}`);
export const exportKPIAnalytics = (format = 'excel') =>
  fetchWithAuthBlob(`/export/kpi-analytics?format=${format}`);

// Project Attachments
export const addProjectAttachment = async (projectId, file) => {
  const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
  const formData = new FormData();
  formData.append('file', file);
  
  const response = await fetch(`${API}/projects/${projectId}/attachments`, {
    method: 'POST',
    headers: {
      ...(token ? { 'Authorization': `Bearer ${token}` } : {})
    },
    body: formData
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Upload failed');
  }
  
  return response.json();
};

export const deleteProjectAttachment = (projectId, attachmentId) =>
  fetchWithAuth(`/projects/${projectId}/attachments/${attachmentId}`, { method: 'DELETE' });

export const getProjectAttachment = (projectId, attachmentId) =>
  fetchWithAuth(`/projects/${projectId}/attachments/${attachmentId}`);

// Export
export const exportData = async (type, format = 'json') => {
  const response = await fetch(`${API}/export/${type}?format=${format}`, {
    headers: getAuthHeaders()
  });

  if (!response.ok) {
    let errorMessage = 'Export failed';
    try {
      const error = await response.json();
      errorMessage = error.detail || errorMessage;
    } catch {
      // Ignore parse errors
    }
    throw new Error(errorMessage);
  }

  // For binary formats, return blob
  if (format === 'excel' || format === 'docx') {
    const blob = await response.blob();
    return blob;
  }

  return response.json();
};


// Expedition Control
export const getExpeditionFields = () =>
  fetchWithAuth('/expedition/fields');
export const createExpeditionField = (data) =>
  fetchWithAuth('/expedition/fields', { method: 'POST', body: JSON.stringify(data) });
export const updateExpeditionField = (fieldId, data) =>
  fetchWithAuth(`/expedition/fields/${fieldId}`, { method: 'PUT', body: JSON.stringify(data) });
export const deleteExpeditionField = (fieldId) =>
  fetchWithAuth(`/expedition/fields/${fieldId}`, { method: 'DELETE' });
export const getExpeditionEntries = (type) =>
  fetchWithAuth(`/expedition/entries${type ? `?type=${type}` : ''}`);
export const createExpeditionEntry = (data) =>
  fetchWithAuth('/expedition/entries', { method: 'POST', body: JSON.stringify(data) });
export const updateExpeditionEntry = (entryId, data) =>
  fetchWithAuth(`/expedition/entries/${entryId}`, { method: 'PUT', body: JSON.stringify(data) });
export const deleteExpeditionEntry = (entryId) =>
  fetchWithAuth(`/expedition/entries/${entryId}`, { method: 'DELETE' });
export const getExpeditionAnalytics = () =>
  fetchWithAuth('/expedition/analytics');

// Expedition Export
export const exportExpedition = (type, format = 'excel') =>
  fetchWithAuthBlob(`/export/expedition/${type}?format=${format}`);

// KPI Import
export const importKPIs = async (file) => {
  const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
  const formData = new FormData();
  formData.append('file', file);
  const response = await fetch(`${API}/kpis/import`, {
    method: 'POST',
    headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
    body: formData,
  });
  if (!response.ok) {
    let msg = 'Import failed';
    try { const err = await response.json(); msg = err.detail || msg; } catch { /* JSON parse fallback */ }
    throw new Error(msg);
  }
  return response.json();
};

export const getPickingTasks = (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.bewerker) params.append('bewerker', filters.bewerker);
  if (filters.bewerker_prefix) params.append('bewerker_prefix', filters.bewerker_prefix);
  if (filters.wachtrij) params.append('wachtrij', filters.wachtrij);
  if (filters.start_from) params.append('start_from', filters.start_from);
  if (filters.start_to) params.append('start_to', filters.start_to);
  if (filters.confirmed_from) params.append('bevestiging_from', filters.confirmed_from);
  if (filters.confirmed_to) params.append('bevestiging_to', filters.confirmed_to);
  const qs = params.toString();
  return fetchWithAuth(`/picking/tasks${qs ? `?${qs}` : ''}`);
};
export const createPickingTask = (data) =>
  fetchWithAuth('/picking/tasks', { method: 'POST', body: JSON.stringify(data) });
export const deletePickingTask = (taskId) =>
  fetchWithAuth(`/picking/tasks/${taskId}`, { method: 'DELETE' });
export const bulkDeletePickingTasks = (dateFrom = '', dateTo = '') => {
  const params = new URLSearchParams();
  if (dateFrom) params.append('date_from', dateFrom);
  if (dateTo) params.append('date_to', dateTo);
  const qs = params.toString();
  return fetchWithAuth(`/picking/tasks-bulk${qs ? `?${qs}` : ''}`, { method: 'DELETE' });
};
export const getPickingAnalytics = (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.bewerker) params.append('bewerker', filters.bewerker);
  if (filters.bewerker_prefix) params.append('bewerker_prefix', filters.bewerker_prefix);
  if (filters.wachtrij) params.append('wachtrij', filters.wachtrij);
  if (filters.start_from) params.append('start_from', filters.start_from);
  if (filters.start_to) params.append('start_to', filters.start_to);
  if (filters.confirmed_from) params.append('bevestiging_from', filters.confirmed_from);
  if (filters.confirmed_to) params.append('bevestiging_to', filters.confirmed_to);
  const qs = params.toString();
  return fetchWithAuth(`/picking/analytics${qs ? `?${qs}` : ''}`);
};
export const exportPickingPerformance = async (filters = {}) => {
  const params = new URLSearchParams();
  ['bewerker', 'bewerker_prefix', 'wachtrij', 'start_from', 'start_to'].forEach((k) => {
    if (filters[k]) params.append(k, filters[k]);
  });
  if (filters.confirmed_from) params.append('bevestiging_from', filters.confirmed_from);
  if (filters.confirmed_to) params.append('bevestiging_to', filters.confirmed_to);
  const qs = params.toString();
  const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
  const r = await fetch(`${API}/picking/analytics/export${qs ? `?${qs}` : ''}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  return r.blob();
};
export const getPickingWorkers = () =>
  fetchWithAuth('/picking/workers');
export const getPickingWachtrijen = () =>
  fetchWithAuth('/picking/wachtrijen');
export const exportHolidays = (params = {}) => {
  const qs = new URLSearchParams();
  if (params.from_date) qs.append('from_date', params.from_date);
  if (params.to_date) qs.append('to_date', params.to_date);
  if (params.department_id && params.department_id !== 'all') qs.append('department_id', params.department_id);
  const q = qs.toString();
  return fetchWithAuthBlob(`/planning/holidays/export${q ? '?' + q : ''}`);
};
export const exportPickingAnalyze = (params = {}) => {
  const qs = new URLSearchParams();
  if (params.date_from) qs.append('date_from', params.date_from);
  if (params.date_to) qs.append('date_to', params.date_to);
  if (params.benchmark_grf != null) qs.append('benchmark_grf', String(params.benchmark_grf));
  if (params.benchmark_grt != null) qs.append('benchmark_grt', String(params.benchmark_grt));
  if (params.pool) qs.append('pool', params.pool);
  if (params.resources) qs.append('resources', params.resources);
  if (params.wachtrij) qs.append('wachtrij', params.wachtrij);
  return fetchWithAuthBlob(`/picking/analyze/export?${qs.toString()}`);
};
// ─── KANRI method teaser: anonymous open counter + owner metrics ──────
const methodSessionId = () => {
  let id = sessionStorage.getItem('kanri_sid');
  if (!id) {
    id = `s${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
    sessionStorage.setItem('kanri_sid', id);
  }
  return id;
};
export const recordMethodView = async (path = '/method') => {
  try {
    await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/method/view`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ session_id: methodSessionId(), path, referrer: document.referrer || null }),
    });
  } catch (_) { /* never block the page on a counter */ }
};
export const getMethodMetrics = (days = 30) => fetchWithAuth(`/method/metrics?days=${days}`);

// ─── Public contact / lead capture (no auth) ──────────────────────────
export const submitContact = async (body) => {
  const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error('contact_failed');
  return res.json();
};

// ─── Order Picking: scanner → pool mapping (GRF used as GRT) ──────────
export const getResourceMap = (params = {}) => {
  const qs = new URLSearchParams(
    Object.fromEntries(Object.entries(params).filter(([, v]) => v))).toString();
  return fetchWithAuth(`/picking/resource-map${qs ? `?${qs}` : ''}`);
};
export const saveResourceRule = (body) =>
  fetchWithAuth('/picking/resource-map/rule', { method: 'PUT', body: JSON.stringify(body) });
export const addResourceMapping = (body) =>
  fetchWithAuth('/picking/resource-map', { method: 'POST', body: JSON.stringify(body) });
export const deleteResourceMapping = (id) =>
  fetchWithAuth(`/picking/resource-map/${id}`, { method: 'DELETE' });
export const importResourceMapFromPresence = (params = {}) => {
  const qs = new URLSearchParams({
    date_from: params.date_from, date_to: params.date_to, apply: String(!!params.apply),
  }).toString();
  return fetchWithAuth(`/picking/resource-map/import-presence?${qs}`, { method: 'POST' });
};

// ─── Order Picking: demand-aligned planning rota ───────────────────────
const rotaQs = (params = {}) => {
  const qs = new URLSearchParams();
  if (params.date_from) qs.append('date_from', params.date_from);
  if (params.date_to) qs.append('date_to', params.date_to);
  if (params.pool) qs.append('pool', params.pool);
  if (params.core_grf) qs.append('core_grf', String(params.core_grf));
  if (params.core_grt) qs.append('core_grt', String(params.core_grt));
  if (params.rate_grf) qs.append('rate_grf', String(params.rate_grf));
  if (params.rate_grt) qs.append('rate_grt', String(params.rate_grt));
  return qs.toString();
};
export const getRotaSettings = () => fetchWithAuth('/picking/rota-settings');
export const saveRotaSettings = (pool, body) =>
  fetchWithAuth(`/picking/rota-settings/${pool}`, { method: 'PUT', body: JSON.stringify(body) });
export const getRotaPlan = (params = {}) => fetchWithAuth(`/picking/rota-plan?${rotaQs(params)}`);
export const exportRotaPlan = (params = {}) =>
  fetchWithAuthBlob(`/picking/rota-plan/export?${rotaQs(params)}`);

export const importPickingTasks = async (file, onProgress) => {
  const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
  const authHeaders = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  const report = (phase, pct, extra = {}) => {
    if (typeof onProgress === 'function') {
      onProgress(typeof onProgress.length === 'number' && onProgress.length >= 2
        ? { phase, percent: Math.max(0, Math.min(100, Math.round(pct))), ...extra }
        : Math.max(0, Math.min(100, Math.round(pct))));
    }
  };

  // PHASE 1 — read the file as base64 (kept here so progress reflects it).
  report('reading', 1);
  const arrayBuffer = await file.arrayBuffer();
  // For very large files, building one giant string with reduce can hit
  // call-stack limits. Build it in 32KB byte chunks instead.
  const bytes = new Uint8Array(arrayBuffer);
  let binary = '';
  const STRIDE = 32 * 1024;
  for (let i = 0; i < bytes.length; i += STRIDE) {
    binary += String.fromCharCode.apply(
      null,
      bytes.subarray(i, Math.min(i + STRIDE, bytes.length)),
    );
  }
  const base64 = btoa(binary);
  report('reading', 5);

  const CHUNK_SIZE = 800_000; // 800KB base64 per chunk → fewer round-trips
  const totalChunks = Math.ceil(base64.length / CHUNK_SIZE);

  // PHASE 2 — init upload session
  let initRes;
  try {
    initRes = await fetch(`${API}/picking/upload/init`, {
      method: 'POST',
      headers: authHeaders,
      body: JSON.stringify({ filename: file.name, total_chunks: totalChunks }),
    });
  } catch {
    throw new Error('Network error: could not start upload');
  }
  if (!initRes.ok) {
    let msg = 'Failed to start upload';
    try { const err = await initRes.json(); msg = err.detail || msg; } catch { /* JSON parse fallback */ }
    throw new Error(msg);
  }
  const { upload_id } = await initRes.json();
  report('uploading', 6, { totalChunks, sentChunks: 0 });

  // PHASE 3 — send chunks with auto-retry on transient failures.
  const sendChunk = async (i, attempt = 0) => {
    const chunk = base64.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
    let res;
    try {
      res = await fetch(`${API}/picking/upload/chunk`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({ upload_id, chunk_index: i, data: chunk }),
      });
    } catch (e) {
      if (attempt < 3) {
        await new Promise(r => setTimeout(r, 400 * (attempt + 1)));
        return sendChunk(i, attempt + 1);
      }
      throw new Error(`Network error uploading chunk ${i + 1}/${totalChunks} (after ${attempt + 1} attempts)`);
    }
    if (!res.ok) {
      // 5xx and 408/429 are transient → retry. 4xx (other) is final.
      const transient = res.status >= 500 || res.status === 408 || res.status === 429;
      if (transient && attempt < 3) {
        await new Promise(r => setTimeout(r, 400 * (attempt + 1)));
        return sendChunk(i, attempt + 1);
      }
      let msg = `Chunk ${i + 1}/${totalChunks} failed (HTTP ${res.status})`;
      try { const err = await res.json(); if (err.detail) msg += ` — ${err.detail}`; } catch { /* JSON parse fallback */ }
      throw new Error(msg);
    }
    return res.json();
  };

  for (let i = 0; i < totalChunks; i++) {
    await sendChunk(i);
    // 5%–90% reserved for the upload phase
    const pct = 5 + ((i + 1) / totalChunks) * 85;
    report('uploading', pct, { totalChunks, sentChunks: i + 1 });
  }

  // PHASE 4 — finalize: this now returns immediately with {upload_id,
  // status:"processing"}. The real work happens in a background task on
  // the server; we poll the status endpoint until phase is "done" or
  // "error". This avoids 504 stream-timeouts on big imports.
  report('processing', 92);
  let finalRes;
  try {
    finalRes = await fetch(`${API}/picking/upload/finalize`, {
      method: 'POST',
      headers: authHeaders,
      body: JSON.stringify({ upload_id }),
    });
  } catch {
    throw new Error('Network error during finalization');
  }
  if (!finalRes.ok) {
    let msg = 'Import finalization failed';
    try { const err = await finalRes.json(); msg = err.detail || msg; } catch { /* JSON parse fallback */ }
    throw new Error(msg);
  }

  // PHASE 5 — poll job status until done or error. We update the
  // user-visible progress bar from the server's reported phase /
  // progress so the user sees parsing → deduping → inserting → done.
  const POLL_INTERVAL_MS = 1500;
  const MAX_POLLS = 600; // 15 minutes at 1.5s per poll
  for (let i = 0; i < MAX_POLLS; i++) {
    await new Promise(r => setTimeout(r, POLL_INTERVAL_MS));
    let s;
    try {
      s = await fetch(`${API}/picking/upload/status/${upload_id}`, { headers: authHeaders });
    } catch {
      // Transient network blip — keep polling instead of throwing.
      continue;
    }
    if (!s.ok) continue;
    const job = await s.json().catch(() => null);
    if (!job) continue;
    const phaseLabel = {
      queued: 'Queued…',
      parsing: 'Parsing Excel…',
      deduping: 'Removing duplicates…',
      inserting: 'Saving to database…',
      done: 'Finishing…',
      error: 'Error',
    }[job.phase] || 'Processing…';
    // Map the server's 0–100 progress (which already accounts for parse
    // + dedup + insert) into the 92–100 sliver we reserved client-side.
    const overall = 92 + ((job.progress || 0) / 100) * 8;
    report(job.phase === 'done' ? 'done' : 'processing', overall, { serverPhase: phaseLabel, serverProgress: job.progress });

    if (job.phase === 'done') {
      report('done', 100);
      return {
        message: job.message,
        count: job.count,
        replaced_existing: job.replaced_existing,
        dates_touched: job.dates_touched,
      };
    }
    if (job.phase === 'error') {
      throw new Error(job.error || 'Import failed');
    }
  }
  throw new Error('Import is taking longer than expected — please refresh the page and check the picking tasks list.');
};

export const comparePickingPeriods = (params) => {
  const qs = new URLSearchParams(params).toString();
  return fetchWithAuth(`/picking/compare?${qs}`);
};

// Picking comparison exports (returns blob for download)
export const exportPickingPeriodComparison = async (params, format = 'excel') => {
  const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
  const qs = new URLSearchParams({ ...params, format }).toString();
  const response = await fetch(`${API}/export/picking/period-comparison?${qs}`, {
    headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
  });
  if (!response.ok) throw new Error('Export failed');
  return response.blob();
};
export const exportPickingBewerkerComparison = async (params, format = 'excel') => {
  const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
  const qs = new URLSearchParams({ ...params, format }).toString();
  const response = await fetch(`${API}/export/picking/bewerker-comparison?${qs}`, {
    headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
  });
  if (!response.ok) throw new Error('Export failed');
  return response.blob();
};
export const exportPickingTasks = async (params) => {
  const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
  const filtered = Object.fromEntries(Object.entries({ ...params, format: 'excel' }).filter(([, v]) => v !== undefined && v !== null && v !== ''));
  const qs = new URLSearchParams(filtered).toString();
  const response = await fetch(`${API}/export/picking/tasks?${qs}`, {
    headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
  });
  if (!response.ok) throw new Error('Export failed');
  return response.blob();
};

// ========== TEAMS ==========
export const getTeams = () => fetchWithAuth('/teams');
export const getTeamsPeoplePool = () => fetchWithAuth('/teams/people-pool');
export const getTeam = (teamId) => fetchWithAuth(`/teams/${teamId}`);
export const createTeam = (data) =>
  fetchWithAuth('/teams', { method: 'POST', body: JSON.stringify(data) });
export const updateTeam = (teamId, data) =>
  fetchWithAuth(`/teams/${teamId}`, { method: 'PUT', body: JSON.stringify(data) });
export const deleteTeam = (teamId) =>
  fetchWithAuth(`/teams/${teamId}`, { method: 'DELETE' });

// ========== CUSTOM CHECKLISTS ==========
export const getCustomChecklists = () => fetchWithAuth('/custom-checklists');

// ========== PRESENCE ==========
export const pingPresence = (resourceType, resourceId, isEditing = false, focusCell = null) => {
  const params = new URLSearchParams({ is_editing: isEditing ? 'true' : 'false' });
  if (focusCell) params.set('focus_cell', focusCell);
  return fetchWithAuth(`/presence/${resourceType}/${resourceId}?${params}`, { method: 'POST' });
};
export const getPresence = (resourceType, resourceId, excludeSelf = true) =>
  fetchWithAuth(`/presence/${resourceType}/${resourceId}?exclude_self=${excludeSelf}`);
export const leavePresence = (resourceType, resourceId) =>
  fetchWithAuth(`/presence/${resourceType}/${resourceId}`, { method: 'DELETE' });

// ========== DAMAGE LOG ==========
export const getDamageLogConfig = () => fetchWithAuth('/damage-logs/config');
export const getDamageLogs = (params = {}) => {
  const qs = new URLSearchParams(Object.fromEntries(Object.entries(params).filter(([, v]) => v))).toString();
  return fetchWithAuth(`/damage-logs${qs ? `?${qs}` : ''}`);
};
export const getDamageLogAnalytics = () => fetchWithAuth('/damage-logs/analytics');
export const createDamageLog = (data) => fetchWithAuth('/damage-logs', { method: 'POST', body: JSON.stringify(data) });
export const updateDamageLog = (id, data) => fetchWithAuth(`/damage-logs/${id}`, { method: 'PUT', body: JSON.stringify(data) });
export const deleteDamageLog = (id) => fetchWithAuth(`/damage-logs/${id}`, { method: 'DELETE' });
